import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { useThemeContext } from '@utils/providers/theme-context.tsx';
import { useTranscript } from '@utils/providers/transcript-context.tsx';
import {
  useContributeSegment,
  ContributeReadiness
} from '@utils/api/custom-models/use-contribute-segment.tsx';
import { useContributeWholeFile } from '@utils/api/custom-models/use-contribute-whole-file.tsx';
import { CustomModelGuideModal } from '@components/custom-models/custom-model-guide-modal.tsx';
import {
  MAX_CONTRIBUTION_MS,
  formatClipDuration,
  summarizeContribution
} from '@utils/editors/training-clip-windows.ts';
import { contributionResultMessage } from '@utils/editors/contribution-messages.ts';
import {
  utteranceStart,
  utteranceStop,
  utteranceText
} from '@utils/editors/transcript-utterances.ts';
import {
  AddToTrainingForm,
  ContributionMode,
  ContributionNote
} from '@components/editors/shared/add-to-training/add-to-training-form.tsx';
import { AddToTrainingResult } from '@components/editors/shared/add-to-training/add-to-training-result.tsx';
import { contributionErrorKey } from '@components/editors/shared/add-to-training/contribution-errors.ts';
import { useContributionModels } from '@components/editors/shared/add-to-training/use-contribution-models.ts';

const GUIDE_SEEN_KEY = 'cm-contribution-guide-seen';

type Props = {
  /** 0-based index of the anchor utterance in ``document.document`` —
   *  the backend uses these positions as the ``segment_ids`` payload for
   *  transcript documents (utterances carry no stable id in the schema). */
  utteranceIndex: number;
  onClose: () => void;
};

export const TranscriptAddToTrainingPanel = ({
  utteranceIndex,
  onClose
}: Props) => {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();
  const {
    session,
    document: transcriptDoc,
    documentResult,
    autoSave
  } = useTranscript();

  const [mode, setMode] = useState<ContributionMode>('only');
  const [result, setResult] = useState<ContributeReadiness | null>(null);
  const [showGuide, setShowGuide] = useState(false);

  const models = useContributionModels(
    session?.language_key ?? session?.language
  );

  const utterances = useMemo(
    () => transcriptDoc?.document ?? [],
    [transcriptDoc]
  );
  const anchorIndex = Math.max(
    0,
    Math.min(utteranceIndex, utterances.length - 1)
  );

  // "only" is the picked utterance; "up-to" everything from the start of the
  // document to it, which the API cuts into clips of at most 30 seconds.
  const windowIndices = useMemo<number[]>(() => {
    if (utterances.length === 0) return [];
    if (mode === 'only') return [anchorIndex];
    return Array.from({ length: anchorIndex + 1 }, (_, i) => i);
  }, [utterances.length, anchorIndex, mode]);

  const summary = useMemo(
    () =>
      summarizeContribution(
        windowIndices.map(i => ({
          start: utteranceStart(utterances[i]),
          stop: utteranceStop(utterances[i])
        }))
      ),
    [windowIndices, utterances]
  );
  // A whole file is the recording as far as the last utterance with text.
  const wholeMs = useMemo(() => {
    const worded = utterances.filter(u => utteranceText(u));
    return worded.length ? utteranceStop(worded[worded.length - 1]) : 0;
  }, [utterances]);
  const isWhole = mode === 'whole';
  // Nothing trainable: the only utterance is longer than one 30s window.
  const overLimit =
    !isWhole && summary.clips === 0 && summary.hasOverlongSegment;
  const tooMuch = !isWhole && summary.ms > MAX_CONTRIBUTION_MS;

  // Keep the picked utterance in view when the mode changes.
  const anchorRowRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    anchorRowRef.current?.scrollIntoView({ block: 'nearest' });
  }, [mode, anchorIndex]);

  const documentId = documentResult?.result?.id ?? '';
  const sessionId = session?.session_id ?? '';
  const contributeSegment = useContributeSegment(sessionId, documentId);
  const contributeWholeFile = useContributeWholeFile(sessionId, documentId);
  const busy = contributeSegment.isLoading || contributeWholeFile.isLoading;
  const submitError = contributeSegment.error ?? contributeWholeFile.error;

  const handleContribute = () => {
    const modelId = models.selectedModelId;
    if (!modelId || !session || !documentId) return;
    // Save silently so the API reads the edited transcript; ``saveDocument``
    // would toast "Document updated" as if the click had only saved.
    autoSave();

    const toastId = toast.loading(t('contribution-submitting'));
    const handlers = {
      onSuccess: (data: ContributeReadiness) => {
        const message = contributionResultMessage(
          data,
          models.selectedModelName
        );
        toast.success(t(message.key, message.values), { id: toastId });
        setResult(data);
        if (!localStorage.getItem(GUIDE_SEEN_KEY)) {
          localStorage.setItem(GUIDE_SEEN_KEY, '1');
          setShowGuide(true);
        }
      },
      onError: () => toast.dismiss(toastId)
    };

    if (isWhole) {
      contributeWholeFile.mutate({ model_id: modelId }, handlers);
      return;
    }
    contributeSegment.mutate(
      {
        model_id: modelId,
        segment_ids: windowIndices,
        anchor_segment_id: anchorIndex
      },
      handlers
    );
  };

  const guide = showGuide && (
    <CustomModelGuideModal
      isOpen={showGuide}
      onClose={() => setShowGuide(false)}
      contributionOnly
    />
  );

  if (result) {
    return (
      <>
        <AddToTrainingResult
          result={result}
          modelName={models.selectedModelName}
          onClose={onClose}
        />
        {guide}
      </>
    );
  }

  const notes: ContributionNote[] = [];
  if (overLimit)
    notes.push({ tone: 'warn', text: t('training-window-too-long') });
  if (tooMuch)
    notes.push({ tone: 'warn', text: t('contribution-long-selection') });
  if (models.isMultilingual && models.eligibleModels.length > 0) {
    notes.push({ tone: 'info', text: t('contribution-multilingual-hint') });
  }

  const modelName = models.selectedModelName;
  const summaryText = !modelName
    ? undefined
    : isWhole
      ? wholeMs > 0
        ? t('contribution-whole-adds', {
            duration: formatClipDuration(wholeMs),
            model: modelName
          })
        : undefined
      : !overLimit && summary.clips > 0
        ? t('contribution-adds', {
            duration: formatClipDuration(summary.ms),
            model: modelName
          })
        : undefined;

  const canSubmit =
    !!models.selectedModelId &&
    !!documentId &&
    !busy &&
    models.eligibleModels.length > 0 &&
    (isWhole
      ? wholeMs > 0
      : !overLimit && summary.clips > 0 && windowIndices.length > 0);

  const accent = getColour('activeText');
  const muted = getColour('inactiveText');
  const border = getColour('border');

  return (
    <>
      <AddToTrainingForm
        models={models}
        mode={mode}
        onModeChange={setMode}
        summary={summaryText}
        notes={notes}
        canSubmit={canSubmit}
        busy={busy}
        onSubmit={handleContribute}
        error={submitError ? t(contributionErrorKey(submitError)) : undefined}
      >
        {windowIndices.map(idx => {
          const isAnchor = idx === anchorIndex;
          const text = utteranceText(utterances[idx]);
          return (
            <div
              key={idx}
              ref={isAnchor ? anchorRowRef : undefined}
              className="flex gap-1.5"
            >
              <div
                className="w-0.5 shrink-0 rounded-full"
                style={{ backgroundColor: isAnchor ? accent : 'transparent' }}
              />
              <div
                className="flex-1 rounded-md border px-2 py-1 text-xs leading-relaxed"
                style={{ borderColor: border }}
              >
                {text || (
                  <span style={{ color: muted }}>
                    {t('empty-utterance-placeholder')}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </AddToTrainingForm>
      {guide}
    </>
  );
};
